import { Vector2 } from "three";
import { PROFILE_GROOVE_POSITIONS } from "../materials/AluminumProfileMaterial";

/**
 * Where the closing profile's grooves go, as points on its cross-section.
 *
 * The outer faces of the section are its two vertical edges at the extreme X:
 * the face on the outside of the case and the face towards its interior, the
 * legs either side of the panel channel. Each gets two grooves, at
 * {@link PROFILE_GROOVE_POSITIONS} of the face's length — the same spacing as
 * the Kantenschutz, centred on the face.
 *
 * Points rather than U values, because the section's developed U is only
 * known once `ProfiledContourGeometry` has built the ring (it may reverse the
 * points and it rounds the corners); the geometry maps these to U with
 * `sectionU`. The fractions are well inside the face, so the die radius at
 * either end never reaches them.
 */
export function outerFaceGroovePoints(
  section: ReadonlyArray<readonly [number, number]>,
  positions: readonly number[] = PROFILE_GROOVE_POSITIONS,
): Vector2[] {
  if (section.length < 3) return [];
  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  for (const [x] of section) {
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
  }
  const points: Vector2[] = [];
  for (const x of [minX, maxX]) {
    const face = longestVerticalEdgeAt(section, x);
    if (!face) continue;
    const [from, to] = face;
    for (const fraction of positions) {
      points.push(new Vector2(x, from[1] + (to[1] - from[1]) * fraction));
    }
  }
  return points;
}

/** Endpoints at the same X, to within a rounding error of a millimetre. */
const SAME_X_EPSILON = 1e-6;

function longestVerticalEdgeAt(
  section: ReadonlyArray<readonly [number, number]>,
  x: number,
): [readonly [number, number], readonly [number, number]] | null {
  let best: [readonly [number, number], readonly [number, number]] | null =
    null;
  let bestLength = 0;
  for (const [i, from] of section.entries()) {
    const to = section[(i + 1) % section.length];
    if (!to) continue;
    if (
      Math.abs(from[0] - x) > SAME_X_EPSILON ||
      Math.abs(to[0] - x) > SAME_X_EPSILON
    ) {
      continue;
    }
    const edgeLength = Math.abs(to[1] - from[1]);
    if (edgeLength > bestLength) {
      best = [from, to];
      bestLength = edgeLength;
    }
  }
  return best;
}
